import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API_URL from "../../apiConfig";

const DESKTOP_PAGE_SIZE = 12;
const MOBILE_PAGE_SIZE = 6;
const MOBILE_PAGE_MEDIA_QUERY = "(max-width: 640px)";
const PAGE_SIZE = DESKTOP_PAGE_SIZE;

const COPY = {
  es: {
    lang: "es",
    locale: "es-CA",
    metaTitle: "ZEDRO News | ZEDRO Academy",
    metaDescription:
      "Ediciones editoriales de ZEDRO Academy para vivir, decidir y construir con intención.",
    fallbackIssueTitle: "Edición ZEDRO News",
    fallbackIssueDescription:
      "Una carta editorial de ZEDRO Academy para vivir, decidir y construir con intención.",
    publishedFallback: "Edición publicada",
    heroTitle: "Ideas claras para ordenar la vida y avanzar con intención.",
    heroSubtitle:
      "Recibe ediciones editoriales de ZEDRO Academy con una idea central, profundidad práctica y una invitación concreta para vivir mejor.",
    filtersKicker: "Buscar ediciones",
    keyword: "Palabra clave",
    keywordPlaceholder: "Ej. enfoque, orden, propósito",
    from: "Desde",
    to: "Hasta",
    apply: "Aplicar filtros",
    clear: "Limpiar filtros",
    frequencyTitle: "Frecuencia editorial",
    frequencyText:
      "ZEDRO News está diseñado como una carta quincenal: una edición, una idea central, una invitación concreta.",
    archiveTitle: "Archivo editorial",
    loading: "Cargando…",
    editionSingular: "edición",
    editionPlural: "ediciones",
    loadErrorTitle: "No se pudieron cargar las ediciones.",
    loadError: "No se pudieron cargar las ediciones.",
    emptyTitle: "Aún no hay ediciones para mostrar.",
    emptyText:
      "Cuando una edición esté publicada, aparecerá aquí automáticamente.",
    readIssue: "Leer edición",
    previous: "Anterior",
    next: "Siguiente",
    page: "Página",
    of: "de",
    basePath: "/zedro-news",
  },
  fr: {
    lang: "fr",
    locale: "fr-CA",
    metaTitle: "ZEDRO News | ZEDRO Academy",
    metaDescription:
      "Éditions éditoriales de ZEDRO Academy pour vivre, décider et construire avec intention.",
    fallbackIssueTitle: "Édition ZEDRO News",
    fallbackIssueDescription:
      "Une lettre éditoriale de ZEDRO Academy pour vivre, décider et construire avec intention.",
    publishedFallback: "Édition publiée",
    heroTitle: "Des idées claires pour ordonner la vie et avancer avec intention.",
    heroSubtitle:
      "Recevez les éditions éditoriales de ZEDRO Academy avec une idée centrale, une profondeur pratique et une invitation concrète à mieux vivre.",
    filtersKicker: "Rechercher des éditions",
    keyword: "Mot-clé",
    keywordPlaceholder: "Ex. attention, ordre, intention",
    from: "Du",
    to: "Au",
    apply: "Appliquer les filtres",
    clear: "Réinitialiser les filtres",
    frequencyTitle: "Fréquence éditoriale",
    frequencyText:
      "ZEDRO News est conçu comme une lettre bimensuelle : une édition, une idée centrale, une invitation concrète.",
    archiveTitle: "Archive éditoriale",
    loading: "Chargement…",
    editionSingular: "édition",
    editionPlural: "éditions",
    loadErrorTitle: "Impossible de charger les éditions.",
    loadError: "Impossible de charger les éditions.",
    emptyTitle: "Aucune édition à afficher pour le moment.",
    emptyText:
      "Lorsqu’une édition en français sera publiée, elle apparaîtra ici automatiquement.",
    readIssue: "Lire l’édition",
    previous: "Précédent",
    next: "Suivant",
    page: "Page",
    of: "sur",
    basePath: "/fr/zedro-news",
  },
};

function cleanLang(value = "es") {
  return String(value || "").trim().toLowerCase() === "fr" ? "fr" : "es";
}

function getCopy(language = "es") {
  return COPY[cleanLang(language)] || COPY.es;
}

function getResponsivePageSize() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return PAGE_SIZE;
  }

  return window.matchMedia(MOBILE_PAGE_MEDIA_QUERY).matches
    ? MOBILE_PAGE_SIZE
    : DESKTOP_PAGE_SIZE;
}

function stripHtml(value = "") {
  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function truncate(value = "", max = 150) {
  const clean = stripHtml(value);
  if (!clean) return "";
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trim()}…`;
}

function getIssueTitle(issue = {}, copy = COPY.es) {
  return (
    issue.hero_title ||
    issue.title ||
    issue.issue_line ||
    copy.fallbackIssueTitle
  );
}

function getIssueDescription(issue = {}, copy = COPY.es) {
  return truncate(
    issue.hero_sub ||
      issue.lead ||
      issue.idea_central ||
      copy.fallbackIssueDescription
  );
}

function getIssueImage(issue = {}) {
  return issue.hero_image || issue.inline_image || "";
}

function formatDate(value, copy = COPY.es) {
  if (!value) return copy.publishedFallback;

  try {
    return new Intl.DateTimeFormat(copy.locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(new Date(value));
  } catch (_error) {
    return copy.publishedFallback;
  }
}

function buildQuery({ page, pageSize, search, dateFrom, dateTo, language }) {
  const params = new URLSearchParams();

  params.set("lang", cleanLang(language));
  params.set("page", String(page || 1));
  params.set("limit", String(pageSize || PAGE_SIZE));

  if (search.trim()) params.set("q", search.trim());
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);

  return params.toString();
}


const responsiveStyles = `
  @media (max-width: 980px) {
    .zedroNewsPublicPage {
      overflow-x: hidden !important;
    }

    .zedroNewsPublicHero {
      padding: 14px 14px 0 !important;
    }

    .zedroNewsPublicHeroInner {
      border-radius: 24px !important;
      padding: 22px 22px 24px !important;
    }

    .zedroNewsPublicHeroTitle {
      font-size: clamp(31px, 9vw, 46px) !important;
      line-height: 1.06 !important;
      max-width: 100% !important;
    }

    .zedroNewsPublicHeroSubtitle {
      max-width: 100% !important;
      font-size: 17px !important;
      line-height: 1.65 !important;
    }

    .zedroNewsPublicShell {
      grid-template-columns: 1fr !important;
      width: 100% !important;
      margin-top: 18px !important;
      padding: 0 14px !important;
      box-sizing: border-box !important;
      overflow: visible !important;
    }

    .zedroNewsPublicFilters {
      position: static !important;
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
      order: 2 !important;
    }

    .zedroNewsPublicContent {
      width: 100% !important;
      min-width: 0 !important;
      order: 1 !important;
    }

    .zedroNewsPublicGrid {
      grid-template-columns: 1fr !important;
      gap: 16px !important;
    }

    .zedroNewsPublicCard {
      grid-template-rows: 190px 1fr !important;
      min-height: auto !important;
      width: 100% !important;
    }

    .zedroNewsPublicCardMeta {
      align-items: flex-start !important;
      flex-direction: column !important;
      gap: 5px !important;
    }
  }

  @media (max-width: 640px) {
    .zedroNewsPublicHero {
      padding: 12px 12px 0 !important;
    }

    .zedroNewsPublicHeroInner {
      border-radius: 22px !important;
      padding: 20px 18px 22px !important;
      box-shadow: 0 18px 44px rgba(15,47,70,.12) !important;
    }

    .zedroNewsPublicHeroBadge {
      min-height: 28px !important;
      padding: 0 12px !important;
      font-size: 12px !important;
      margin-bottom: 12px !important;
    }

    .zedroNewsPublicHeroTitle {
      font-size: clamp(32px, 10vw, 43px) !important;
      line-height: 1.08 !important;
      letter-spacing: -.052em !important;
      margin-bottom: 12px !important;
    }

    .zedroNewsPublicHeroSubtitle {
      font-size: 16px !important;
      line-height: 1.62 !important;
    }

    .zedroNewsPublicShell {
      padding: 0 12px !important;
      gap: 18px !important;
      margin-top: 18px !important;
    }

    .zedroNewsPublicContentHeader {
      display: grid !important;
      grid-template-columns: 1fr auto !important;
      gap: 12px !important;
      align-items: start !important;
      margin-bottom: 14px !important;
    }

    .zedroNewsPublicContentTitle {
      font-size: 34px !important;
      line-height: 1.08 !important;
    }

    .zedroNewsPublicCounter {
      margin-top: 4px !important;
      white-space: nowrap !important;
    }

    .zedroNewsPublicFilters {
      border-radius: 24px !important;
      padding: 18px !important;
    }

    .zedroNewsPublicFiltersTitle {
      font-size: 28px !important;
    }

    .zedroNewsPublicFilters form,
    .zedroNewsPublicFilters label {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box !important;
    }

    .zedroNewsPublicInput {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      min-height: 48px !important;
      font-size: 16px !important;
      box-sizing: border-box !important;
    }

    input[type="date"].zedroNewsPublicInput {
      display: block !important;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box !important;
      -webkit-appearance: none !important;
      appearance: none !important;
    }

    .zedroNewsPublicPrimaryButton,
    .zedroNewsPublicClearButton {
      min-height: 48px !important;
      font-size: 15px !important;
    }

    .zedroNewsPublicNoteBox {
      display: none !important;
    }

    .zedroNewsPublicCard {
      grid-template-rows: 210px 1fr !important;
      border-radius: 24px !important;
    }

    .zedroNewsPublicCardBody {
      padding: 18px !important;
    }

    .zedroNewsPublicCardTitle {
      font-size: 24px !important;
    }

    .zedroNewsPublicCardText {
      font-size: 15px !important;
    }

    .zedroNewsPublicPagination {
      flex-wrap: wrap !important;
      gap: 10px !important;
    }
  }

  @media (max-width: 420px) {
    .zedroNewsPublicHeroTitle {
      font-size: 34px !important;
    }

    .zedroNewsPublicHeroSubtitle {
      font-size: 15.5px !important;
    }

    .zedroNewsPublicContentHeader {
      grid-template-columns: 1fr !important;
    }

    .zedroNewsPublicCounter {
      width: fit-content !important;
    }

    .zedroNewsPublicCard {
      grid-template-rows: 185px 1fr !important;
    }
  }
`;

export default function ZedroNewsPage({ language = "es", basePath = "" }) {
  const copy = getCopy(language);
  const lang = cleanLang(language);
  const issueBasePath = basePath || copy.basePath;
  const [pageSize, setPageSize] = useState(() => getResponsivePageSize());

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: pageSize,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false,
  });

  const [filters, setFilters] = useState({
    search: "",
    dateFrom: "",
    dateTo: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    dateFrom: "",
    dateTo: "",
  });

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const hasFilters = useMemo(() => {
    return Boolean(
      appliedFilters.search.trim() ||
        appliedFilters.dateFrom ||
        appliedFilters.dateTo
    );
  }, [appliedFilters]);

  useEffect(() => {
    document.title = copy.metaTitle;

    let description = document.querySelector('meta[name="description"]');

    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }

    description.setAttribute("content", copy.metaDescription);
  }, [copy.metaDescription, copy.metaTitle]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_PAGE_MEDIA_QUERY);

    const updatePageSize = () => {
      setPageSize(mediaQuery.matches ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE);
    };

    updatePageSize();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updatePageSize);
      return () => mediaQuery.removeEventListener("change", updatePageSize);
    }

    mediaQuery.addListener(updatePageSize);
    return () => mediaQuery.removeListener(updatePageSize);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  useEffect(() => {
    let cancelled = false;

    const loadIssues = async () => {
      try {
        setLoading(true);
        setError("");

        const query = buildQuery({
          page,
          pageSize,
          search: appliedFilters.search,
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
          language: lang,
        });

        const res = await fetch(`${API_URL}/api/editorials/public?${query}`);
        const json = await res.json().catch(() => null);

        if (!res.ok || !json?.ok) {
          throw new Error(json?.error || copy.loadError);
        }

        if (!cancelled) {
          setItems(Array.isArray(json.items) ? json.items : []);
          setPagination({
            page: json.pagination?.page || page,
            limit: json.pagination?.limit || pageSize,
            total: json.pagination?.total || 0,
            total_pages: json.pagination?.total_pages || 1,
            has_next: Boolean(json.pagination?.has_next),
            has_prev: Boolean(json.pagination?.has_prev),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || copy.loadError);
          setItems([]);
          setPagination((current) => ({
            ...current,
            total: 0,
            total_pages: 1,
            has_next: false,
            has_prev: false,
          }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadIssues();

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, appliedFilters, lang, copy.loadError]);

  const onSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({
      search: filters.search,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
    });
  };

  const clearFilters = () => {
    const clean = {
      search: "",
      dateFrom: "",
      dateTo: "",
    };

    setFilters(clean);
    setAppliedFilters(clean);
    setPage(1);
  };

  return (
    <main className="zedroNewsPublicPage" style={{ ...styles.page, overflowX: "hidden" }}>
      <style>{responsiveStyles}</style>
      <section className="zedroNewsPublicHero" style={styles.hero}>
        <div className="zedroNewsPublicHeroInner" style={styles.heroInner}>
          <span className="zedroNewsPublicHeroBadge" style={styles.heroBadge}>ZEDRO News</span>

          <h1 className="zedroNewsPublicHeroTitle" style={styles.heroTitle}>{copy.heroTitle}</h1>

          <p className="zedroNewsPublicHeroSubtitle" style={styles.heroSubtitle}>{copy.heroSubtitle}</p>
        </div>
      </section>

      <section className="zedroNewsPublicShell" style={styles.shell}>
        <aside className="zedroNewsPublicFilters" style={styles.filtersCard}>
          <div style={styles.filtersHeader}>
            <p style={styles.filtersKicker}>{copy.filtersKicker}</p>
            <h2 className="zedroNewsPublicFiltersTitle" style={styles.filtersTitle}>ZEDRO News</h2>
          </div>

          <form onSubmit={onSubmit} style={styles.form}>
            <label style={styles.label}>
              {copy.keyword}
              <input
                value={filters.search}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    search: event.target.value,
                  }))
                }
                placeholder={copy.keywordPlaceholder}
                className="zedroNewsPublicInput"
                style={styles.input}
              />
            </label>

            <label style={styles.label}>
              {copy.from}
              <input
                type="date"
                value={filters.dateFrom}
                className="zedroNewsPublicInput"
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    dateFrom: event.target.value,
                  }))
                }
                style={styles.input}
              />
            </label>

            <label style={styles.label}>
              {copy.to}
              <input
                type="date"
                value={filters.dateTo}
                className="zedroNewsPublicInput"
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    dateTo: event.target.value,
                  }))
                }
                style={styles.input}
              />
            </label>

            <button type="submit" className="zedroNewsPublicPrimaryButton" style={styles.primaryButton}>
              {copy.apply}
            </button>

            {hasFilters && (
              <button type="button" onClick={clearFilters} className="zedroNewsPublicClearButton" style={styles.clearButton}>
                {copy.clear}
              </button>
            )}
          </form>

          <div className="zedroNewsPublicNoteBox" style={styles.noteBox}>
            <strong>{copy.frequencyTitle}</strong>
            <span>{copy.frequencyText}</span>
          </div>
        </aside>

        <section className="zedroNewsPublicContent" style={styles.content}>
          <div className="zedroNewsPublicContentHeader" style={styles.contentHeader}>
            <h2 className="zedroNewsPublicContentTitle" style={styles.contentTitle}>{copy.archiveTitle}</h2>

            <div className="zedroNewsPublicCounter" style={styles.counter}>
              {loading
                ? copy.loading
                : `${pagination.total} ${
                    pagination.total === 1
                      ? copy.editionSingular
                      : copy.editionPlural
                  }`}
            </div>
          </div>

          {error && (
            <div style={styles.messageCard}>
              <strong>{copy.loadErrorTitle}</strong>
              <span>{error}</span>
            </div>
          )}

          {!error && loading && (
            <div className="zedroNewsPublicGrid" style={styles.grid}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} style={styles.skeletonCard}>
                  <div style={styles.skeletonImage} />
                  <div style={styles.skeletonLineLarge} />
                  <div style={styles.skeletonLine} />
                  <div style={styles.skeletonLineSmall} />
                </div>
              ))}
            </div>
          )}

          {!error && !loading && !items.length && (
            <div style={styles.emptyCard}>
              <p style={styles.emptyTitle}>{copy.emptyTitle}</p>
              <p style={styles.emptyText}>{copy.emptyText}</p>
            </div>
          )}

          {!error && !loading && Boolean(items.length) && (
            <>
              <div className="zedroNewsPublicGrid" style={styles.grid}>
                {items.map((issue) => {
                  const image = getIssueImage(issue);
                  const title = getIssueTitle(issue, copy);
                  const description = getIssueDescription(issue, copy);
                  const date = formatDate(issue.published_at || issue.created_at, copy);

                  return (
                    <Link
                      key={issue.id || issue.slug}
                      to={`${issueBasePath}/${issue.slug}`}
                      className="zedroNewsPublicCard"
                      style={styles.card}
                    >
                      <div style={styles.cardImageWrap}>
                        {image ? (
                          <img src={image} alt="" style={styles.cardImage} loading="lazy" />
                        ) : (
                          <div style={styles.cardImageFallback}>ZEDRO News</div>
                        )}
                      </div>

                      <div className="zedroNewsPublicCardBody" style={styles.cardBody}>
                        <div className="zedroNewsPublicCardMeta" style={styles.cardMeta}>
                          <span>{issue.issue_line || "ZEDRO News"}</span>
                          <span>{date}</span>
                        </div>

                        <h3 className="zedroNewsPublicCardTitle" style={styles.cardTitle}>{title}</h3>

                        {description && (
                          <p className="zedroNewsPublicCardText" style={styles.cardText}>{description}</p>
                        )}

                        <span style={styles.cardLink}>{copy.readIssue}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>

              <div className="zedroNewsPublicPagination" style={styles.pagination}>
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.max(current - 1, 1))}
                  disabled={!pagination.has_prev}
                  style={{
                    ...styles.pageButton,
                    ...(!pagination.has_prev ? styles.pageButtonDisabled : {}),
                  }}
                >
                  {copy.previous}
                </button>

                <span style={styles.pageStatus}>
                  {copy.page} {pagination.page} {copy.of} {pagination.total_pages}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    setPage((current) =>
                      Math.min(current + 1, pagination.total_pages || current + 1)
                    )
                  }
                  disabled={!pagination.has_next}
                  style={{
                    ...styles.pageButton,
                    ...(!pagination.has_next ? styles.pageButtonDisabled : {}),
                  }}
                >
                  {copy.next}
                </button>
              </div>
            </>
          )}
        </section>
      </section>
    </main>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #f7f9fc 0%, #ffffff 44%, #f6f8fb 100%)",
    color: "#1f2f40",
    paddingBottom: 52,
  },

  hero: {
    padding: "18px 18px 0",
  },

  heroInner: {
    width: "min(1180px, 100%)",
    margin: "0 auto",
    borderRadius: 26,
    background:
      "linear-gradient(135deg, #0f2f46 0%, #173f5e 52%, #0f2f46 100%)",
    color: "#fff",
    boxShadow: "0 22px 60px rgba(15,47,70,.13)",
    padding: "22px 26px 24px",
  },

  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 30,
    padding: "0 13px",
    borderRadius: 999,
    background: "rgba(244,210,122,.12)",
    border: "1px solid rgba(244,210,122,.30)",
    color: "#f4d27a",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: ".03em",
    marginBottom: 11,
  },

  heroTitle: {
    margin: "0 0 9px",
    maxWidth: 980,
    fontSize: "clamp(28px, 4.25vw, 48px)",
    lineHeight: 1.04,
    letterSpacing: "-.045em",
    color: "#ffffff",
  },

  heroSubtitle: {
    margin: 0,
    maxWidth: 820,
    color: "rgba(255,255,255,.88)",
    fontSize: "clamp(15px, 1.7vw, 17px)",
    lineHeight: 1.55,
  },

  shell: {
    width: "min(1180px, 100%)",
    margin: "20px auto 0",
    padding: "0 18px",
    display: "grid",
    gridTemplateColumns: "310px minmax(0, 1fr)",
    gap: 22,
    alignItems: "start",
  },

  filtersCard: {
    position: "sticky",
    top: 22,
    borderRadius: 26,
    background: "#fff",
    border: "1px solid rgba(44,59,81,.10)",
    boxShadow: "0 18px 44px rgba(15,47,70,.07)",
    padding: 20,
  },

  filtersHeader: {
    marginBottom: 18,
  },

  filtersKicker: {
    margin: "0 0 6px",
    color: "#b99a43",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: ".16em",
    textTransform: "uppercase",
  },

  filtersTitle: {
    margin: 0,
    color: "#0f2f46",
    fontSize: 24,
    letterSpacing: "-.03em",
  },

  form: {
    display: "grid",
    gap: 14,
  },

  label: {
    display: "grid",
    gap: 7,
    color: "rgba(31,47,64,.78)",
    fontSize: 12,
    fontWeight: 800,
  },

  input: {
    width: "100%",
    minHeight: 44,
    borderRadius: 14,
    border: "1px solid rgba(44,59,81,.14)",
    background: "#f9fbfd",
    color: "#1f2f40",
    padding: "0 13px",
    outline: "none",
    fontSize: 14,
    boxSizing: "border-box",
  },

  primaryButton: {
    minHeight: 44,
    border: 0,
    borderRadius: 999,
    background: "#0f2f46",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 12px 28px rgba(15,47,70,.16)",
  },

  clearButton: {
    minHeight: 42,
    borderRadius: 999,
    border: "1px solid rgba(44,59,81,.14)",
    background: "#fff",
    color: "#0f2f46",
    fontWeight: 900,
    cursor: "pointer",
  },

  noteBox: {
    marginTop: 18,
    borderRadius: 18,
    background: "linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%)",
    border: "1px solid rgba(44,59,81,.08)",
    padding: 15,
    display: "grid",
    gap: 7,
    color: "rgba(31,47,64,.70)",
    lineHeight: 1.55,
    fontSize: 12,
  },

  content: {
    minWidth: 0,
  },

  contentHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 18,
  },

  contentTitle: {
    margin: 0,
    color: "#0f2f46",
    fontSize: "clamp(28px, 3vw, 42px)",
    letterSpacing: "-.04em",
  },

  counter: {
    minHeight: 30,
    display: "inline-flex",
    alignItems: "center",
    padding: "0 12px",
    borderRadius: 999,
    background: "#fff",
    border: "1px solid rgba(44,59,81,.10)",
    color: "rgba(31,47,64,.70)",
    fontWeight: 900,
    fontSize: 12,
    whiteSpace: "nowrap",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 18,
  },

  card: {
    display: "grid",
    gridTemplateRows: "170px 1fr",
    minHeight: 372,
    overflow: "hidden",
    borderRadius: 24,
    background: "#fff",
    border: "1px solid rgba(44,59,81,.10)",
    color: "inherit",
    textDecoration: "none",
    boxShadow: "0 18px 44px rgba(15,47,70,.06)",
    transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
  },

  cardImageWrap: {
    background:
      "linear-gradient(135deg, rgba(15,47,70,.10), rgba(244,210,122,.16))",
    overflow: "hidden",
  },

  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  cardImageFallback: {
    height: "100%",
    display: "grid",
    placeItems: "center",
    color: "#0f2f46",
    fontWeight: 900,
    letterSpacing: ".12em",
    textTransform: "uppercase",
    fontSize: 12,
  },

  cardBody: {
    padding: 18,
    display: "grid",
    alignContent: "start",
    gap: 11,
  },

  cardMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    color: "#b99a43",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: ".08em",
    textTransform: "uppercase",
  },

  cardTitle: {
    margin: 0,
    color: "#0f2f46",
    fontSize: 21,
    lineHeight: 1.18,
    letterSpacing: "-.035em",
  },

  cardText: {
    margin: 0,
    color: "rgba(31,47,64,.68)",
    lineHeight: 1.55,
    fontSize: 14,
  },

  cardLink: {
    marginTop: 4,
    color: "#0f2f46",
    fontWeight: 900,
    fontSize: 12,
  },

  pagination: {
    marginTop: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },

  pageButton: {
    minHeight: 40,
    padding: "0 16px",
    borderRadius: 999,
    border: "1px solid rgba(44,59,81,.13)",
    background: "#fff",
    color: "#0f2f46",
    fontWeight: 900,
    cursor: "pointer",
  },

  pageButtonDisabled: {
    opacity: 0.42,
    cursor: "not-allowed",
  },

  pageStatus: {
    color: "rgba(31,47,64,.68)",
    fontWeight: 800,
    fontSize: 12,
  },

  messageCard: {
    borderRadius: 22,
    background: "#fff",
    border: "1px solid rgba(122,43,43,.16)",
    color: "#7a2b2b",
    padding: 18,
    display: "grid",
    gap: 6,
    boxShadow: "0 18px 44px rgba(15,47,70,.05)",
  },

  emptyCard: {
    borderRadius: 24,
    background: "#fff",
    border: "1px solid rgba(44,59,81,.10)",
    padding: 28,
    textAlign: "center",
    boxShadow: "0 18px 44px rgba(15,47,70,.05)",
  },

  emptyTitle: {
    margin: "0 0 8px",
    color: "#0f2f46",
    fontWeight: 900,
    fontSize: 22,
  },

  emptyText: {
    margin: 0,
    color: "rgba(31,47,64,.68)",
    lineHeight: 1.6,
  },

  skeletonCard: {
    minHeight: 360,
    borderRadius: 24,
    background: "#fff",
    border: "1px solid rgba(44,59,81,.08)",
    padding: 14,
    display: "grid",
    alignContent: "start",
    gap: 12,
  },

  skeletonImage: {
    height: 150,
    borderRadius: 18,
    background: "linear-gradient(90deg, #eef2f6, #f7f9fc, #eef2f6)",
  },

  skeletonLineLarge: {
    height: 22,
    borderRadius: 999,
    background: "#eef2f6",
  },

  skeletonLine: {
    height: 14,
    borderRadius: 999,
    background: "#eef2f6",
  },

  skeletonLineSmall: {
    width: "55%",
    height: 14,
    borderRadius: 999,
    background: "#eef2f6",
  },
};
